<script setup lang="ts">
/**
 * /ai-cover-letter: the letter as a PDF in the design of the CV it was written
 * from (server/api/cover-letters/[id]/pdf.get.ts), with a drawn mock of the
 * page rather than a screenshot, so it follows the theme and never goes stale.
 *
 * The anchor #cover-letter-pdf is linked from the homepage (components/sofax/EditorExtrasSection.vue).
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const points = ['header', 'design', 'language', 'free'] as const
</script>

<template>
  <section id="cover-letter-pdf" class="sofax surface-alt scroll-mt-24">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <div class="eyebrow mb-5">{{ t('coverLetterPdf.eyebrow') }}</div>
            <h2 class="mb-5 !text-3xl md:!text-[2.5rem]">{{ t('coverLetterPdf.title') }}</h2>
            <p class="!mb-8 text-lg">{{ t('coverLetterPdf.text') }}</p>
            <ul class="mb-10 flex flex-col gap-3">
              <li v-for="point in points" :key="point" class="flex items-start gap-3">
                <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent" />
                <span class="text-base">{{ t(`coverLetterPdf.points.${point}`) }}</span>
              </li>
            </ul>
            <SofaxBtn :to="isAuthenticated ? '/cv' : '/registration'" :label="t('coverLetterPdf.cta')" variant="dark" />
          </div>

          <!-- A drawn page: the CV's band header, then the letter -->
          <div class="mx-auto w-full max-w-[26rem]" aria-hidden="true">
            <div class="overflow-hidden rounded-[0.625rem] bg-white shadow-lift">
              <div class="bg-[#1d4ed8] px-7 pb-5 pt-7 text-white">
                <div class="font-title text-xl font-bold leading-tight">Jana Nováková</div>
                <div class="mt-1 text-sm opacity-85">Senior Frontend Developer</div>
                <div class="mt-3 text-xs opacity-80">jana@example.com · +420 777 123 456 · Praha</div>
              </div>
              <div class="px-7 pb-8 pt-6 text-ink">
                <div class="flex items-start justify-between text-xs">
                  <span class="font-bold">TechCorp s.r.o.</span>
                  <span class="text-muted">30. září 2026</span>
                </div>
                <div class="mt-5 text-sm font-bold text-[#1d4ed8]">{{ t('coverLetterPdf.mock.subject') }}</div>
                <div class="mt-4 space-y-2">
                  <div class="h-2 w-full rounded bg-line" />
                  <div class="h-2 w-11/12 rounded bg-line" />
                  <div class="h-2 w-4/5 rounded bg-line" />
                </div>
                <div class="mt-4 space-y-2">
                  <div class="h-2 w-full rounded bg-line" />
                  <div class="h-2 w-10/12 rounded bg-line" />
                  <div class="h-2 w-11/12 rounded bg-line" />
                  <div class="h-2 w-2/3 rounded bg-line" />
                </div>
                <div class="mt-6 text-sm font-bold">Jana Nováková</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
